import { OnDestroy, Pipe, PipeTransform } from '@angular/core';
import type { SafeHtml } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, map, startWith, tap } from 'rxjs/operators';

import { TranslateService } from '@main-client/src/app/translate/translate.service';

import { ContentLocalization } from '@libs/src/locale/locale.interface';
import { Group } from '@libs/src/models/group.model';
import { IAppState } from '@main-client/src/app/app.state';

import { LocaleManagerModalService } from '../../locale-manager/locale-manager-modal.service';

@Pipe({
  name: 'liveTranslate',
})
export class LiveTranslatePipe implements PipeTransform, OnDestroy {
  content: ContentLocalization['source'];
  groupId: Group['_id'];

  constructor(
    private readonly localeManagerModalService: LocaleManagerModalService,
    private readonly store: Store<IAppState>,
    private readonly translateService: TranslateService,
  ) {
    this.store
      .select('currentGroup')
      .pipe(
        tap((groupId) => {
          this.groupId = groupId;
        }),
      )
      .subscribe();
  }

  transform<T extends SafeHtml | string>(
    content: T,
    groupId?: Group['_id'],
    translationUnit?: ContentLocalization['translation_unit'],
    translationUnitIdentifier?: string,
  ): Observable<T> {
    this.content = content as string;
    return this.localeManagerModalService
      .listenToLiveTranslatePreference()
      .pipe(
        concatMap((liveTranslatePreference) => {
          const untranslatedItem = {
            group: groupId || this.groupId,
            source: content as string,
            ...(translationUnit &&
              translationUnitIdentifier && {
                translation_unit: translationUnit,
                translation_unit_identifier: translationUnitIdentifier,
              }),
          };
          if (!content || this.translateService.$isUserUsingDefaultLocale()) {
            return of(content);
          }
          return this.translateService
            .liveTranslate(untranslatedItem, liveTranslatePreference)
            .pipe(
              map((translatedItem) =>
                liveTranslatePreference && translatedItem?.target
                  ? (translatedItem.target as T)
                  : (translatedItem.source as T),
              ),
              catchError(() => of(content)),
            );
        }),
        startWith(content),
      );
  }

  ngOnDestroy(): void {
    this.translateService.removeFromLiveTranslationsInView({
      source: this.content,
    });
  }
}
